import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Minus, Calendar, RefreshCw,
  AlertTriangle, CheckCircle, XCircle, Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useAdminSettings } from '../../../hooks/useAdminSettings';

// Chart colors
const COLORS = {
  approved: '#22c55e',
  rejected: '#ef4444',
  flagged: '#f59e0b',
  total: '#8b5cf6',
  // Category colors
  self_harm: '#dc2626',
  racism: '#ea580c',
  child_abuse: '#b91c1c',
  animal_abuse: '#c2410c',
  copyright_infringement: '#0891b2',
  terrorism: '#7c2d12',
  drug_promotion: '#4f46e5',
  spam: '#6b7280',
  scam: '#92400e',
  impersonation: '#7c3aed'
};

const CATEGORY_COLORS = [
  '#dc2626', '#ea580c', '#f59e0b', '#84cc16', '#22c55e',
  '#14b8a6', '#0891b2', '#3b82f6', '#6366f1', '#8b5cf6'
];

const ModerationCharts = () => {
  const { apiCall } = useAdminSettings();
  const [timelineData, setTimelineData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [granularity, setGranularity] = useState('day');

  const fetchTimelineData = useCallback(async () => {
    try {
      const data = await apiCall('get', `/api/moderation/stats/timeline?days=${days}&granularity=${granularity}`);
      setTimelineData(data);
    } catch (error) {
      console.error('Error fetching timeline data:', error);
    }
  }, [apiCall, days, granularity]);

  const fetchCategoryData = useCallback(async () => {
    try {
      const data = await apiCall('get', `/api/moderation/stats/categories/timeline?days=${days}`);
      setCategoryData(data);
    } catch (error) {
      console.error('Error fetching category data:', error);
    }
  }, [apiCall, days]);

  const fetchSummaryData = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/moderation/stats/summary');
      setSummaryData(data);
    } catch (error) {
      console.error('Error fetching summary data:', error);
    }
  }, [apiCall]);

  const refreshData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchTimelineData(), fetchCategoryData(), fetchSummaryData()]);
    setLoading(false);
  }, [fetchTimelineData, fetchCategoryData, fetchSummaryData]);

  useEffect(() => {
    refreshData();
  }, [days, granularity]);

  const TrendIndicator = ({ value, suffix = '' }) => {
    if (value === 0) return <Minus className="w-4 h-4 text-gray-400" />;
    if (value > 0) return (
      <span className="flex items-center text-red-400 text-sm">
        <TrendingUp className="w-4 h-4 mr-1" />
        +{value}{suffix}
      </span>
    );
    return (
      <span className="flex items-center text-green-400 text-sm">
        <TrendingDown className="w-4 h-4 mr-1" />
        {value}{suffix}
      </span>
    );
  };

  const StatCard = ({ title, value, change, icon: Icon, color }) => (
    <Card className="bg-gray-800/50 border-gray-700">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400">{title}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            {change !== undefined && <TrendIndicator value={change} suffix="%" />}
          </div>
          <Icon className={`w-8 h-8 ${color} opacity-50`} />
        </div>
      </CardContent>
    </Card>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl">
        <p className="text-sm font-medium text-white mb-2">{label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey || entry.name} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  };

  if (loading && !timelineData) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-yellow-400" />
            Moderation Trends
          </h2>
          <p className="text-sm text-gray-400">Violation patterns and moderation activity over time</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-32 bg-gray-800 border-gray-700">
              <Calendar className="w-4 h-4 mr-2 text-gray-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="14">14 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={granularity} onValueChange={setGranularity}>
            <SelectTrigger className="w-28 bg-gray-800 border-gray-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="hour">Hourly</SelectItem>
              <SelectItem value="day">Daily</SelectItem>
              <SelectItem value="week">Weekly</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="icon"
            onClick={refreshData}
            disabled={loading}
            className="border-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summaryData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Moderated"
            value={summaryData.current.total}
            change={summaryData.changes.total}
            icon={Activity}
            color="text-purple-400"
          />
          <StatCard
            title="Approved"
            value={summaryData.current.approved}
            icon={CheckCircle}
            color="text-green-400"
          />
          <StatCard
            title="Rejected"
            value={summaryData.current.rejected}
            change={summaryData.changes.rejected}
            icon={XCircle}
            color="text-red-400"
          />
          <StatCard
            title="Rejection Rate"
            value={`${summaryData.current.rejection_rate}%`}
            change={summaryData.changes.rejection_rate}
            icon={AlertTriangle}
            color="text-yellow-400"
          />
        </div>
      )}

      {/* Main Timeline Chart */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Moderation Activity Over Time</CardTitle>
          <CardDescription>
            {timelineData?.data_points || 0} data points | {days} days | {granularity}ly
          </CardDescription>
        </CardHeader>
        <CardContent>
          {timelineData?.timeline?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={timelineData.timeline} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorApproved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.approved} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.approved} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorRejected" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.rejected} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.rejected} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorFlagged" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.flagged} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.flagged} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickFormatter={(value) => {
                    if (granularity === 'day') return value.slice(5);
                    return value;
                  }}
                />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="approved"
                  name="Approved"
                  stroke={COLORS.approved}
                  fillOpacity={1}
                  fill="url(#colorApproved)"
                />
                <Area
                  type="monotone"
                  dataKey="rejected"
                  name="Rejected"
                  stroke={COLORS.rejected}
                  fillOpacity={1}
                  fill="url(#colorRejected)"
                />
                <Area
                  type="monotone"
                  dataKey="flagged"
                  name="Flagged"
                  stroke={COLORS.flagged}
                  fillOpacity={1}
                  fill="url(#colorFlagged)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No moderation data available for this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Categories Chart and Top Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Timeline */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Violations by Category</CardTitle>
            <CardDescription>Stacked view of flagged content categories</CardDescription>
          </CardHeader>
          <CardContent>
            {categoryData?.timeline?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryData.timeline} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#9ca3af" 
                    fontSize={12}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {categoryData.categories.slice(0, 6).map((category, index) => (
                    <Bar
                      key={category}
                      dataKey={category}
                      name={category.replace(/_/g, ' ')}
                      stackId="categories"
                      fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                No category data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Categories Pie Chart */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Top Violation Categories</CardTitle>
            <CardDescription>Most common violations this week</CardDescription>
          </CardHeader>
          <CardContent>
            {summaryData?.top_categories?.length > 0 ? (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="60%" height={200}>
                  <PieChart>
                    <Pie
                      data={summaryData.top_categories}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      dataKey="count"
                      nameKey="category"
                      label={({ category, percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      {summaryData.top_categories.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {summaryData.top_categories.map((cat, index) => (
                    <div key={cat.category} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                        />
                        <span className="text-sm text-gray-300 capitalize">
                          {cat.category.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {cat.count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                No violations recorded this week
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trend Analysis */}
      {timelineData?.timeline?.length > 1 && (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Rejection Rate Trend</CardTitle>
            <CardDescription>Percentage of content rejected over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart 
                data={timelineData.timeline.map(d => ({
                  ...d,
                  rejectionRate: d.total > 0 ? ((d.rejected / d.total) * 100).toFixed(1) : 0
                }))}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickFormatter={(value) => value.slice(5)}
                />
                <YAxis 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  content={<CustomTooltip />}
                  formatter={(value) => [`${value}%`, 'Rejection Rate']}
                />
                <Line
                  type="monotone"
                  dataKey="rejectionRate"
                  name="Rejection Rate"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ fill: '#f59e0b', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ModerationCharts;
